import { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Search, X, Edit2, Trash2, Boxes, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DatePickerField from "@/components/DatePickerField";
import ExportBar from "@/components/ExportBar";
import ReceiptUploader from "@/components/ReceiptUploader";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatCurrency } from "@/lib/formatters";
import * as db from "@/lib/db";

const emptyAsset = () => ({
  id: null,
  purchaseDate: new Date().toISOString().slice(0, 10),
  name: "",
  supplier: "",
  category: "",
  purchaseAmount: "",
  businessUsePercentage: "100",
  receiptNumber: "",
  assetStatusId: "",
  disposalDate: "",
  taxCapitalAllowanceStatus: "",
  linkedTransactionId: "",
  notes: "",
});

const Assets = () => {
  const [assets, setAssets] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [assetTransactions, setAssetTransactions] = useState([]); // asset_capitalized txs eligible for linking
  const [types, setTypes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyAsset());
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchDeb, setSearchDeb] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDeb(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const fetchStatic = useCallback(async () => {
    try {
      const [sts, ttypes] = await Promise.all([
        db.listAssetStatuses({ includeArchived: true }),
        db.listTransactionTypes({ includeArchived: true }),
      ]);
      setStatuses(sts); setTypes(ttypes);
      // Load capitalised transactions available for linking.
      const assetType = ttypes.find((t) => t.plTreatment === "asset_capitalized" && !t.isArchived);
      if (assetType) {
        const txs = await db.listTransactions({ transactionTypeId: assetType.id, sortBy: "date_desc" });
        setAssetTransactions(txs);
      }
    } catch (e) { console.error(e); }
  }, []);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const rows = await db.listAssets({ statusId: statusFilter, search: searchDeb, sortBy: "date_desc" });
      setAssets(rows);
    } catch (e) { console.error(e); toast.error("Failed to load assets"); }
    finally { setLoading(false); }
  }, [statusFilter, searchDeb]);

  useEffect(() => { fetchStatic(); }, [fetchStatic]);
  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const total = useMemo(() => assets.reduce((s, a) => s + Number(a.purchaseAmount || 0), 0), [assets]);
  const activeStatusId = useMemo(() => statuses.find((s) => s.name.toLowerCase() === "active")?.id, [statuses]);
  const activeAssets = useMemo(() => assets.filter((a) => a.assetStatusId === activeStatusId), [assets, activeStatusId]);
  const activeValue = useMemo(() => activeAssets.reduce((s, a) => s + Number(a.purchaseAmount || 0), 0), [activeAssets]);

  const openNew = () => {
    const activeId = activeStatusId || (statuses[0]?.id || "");
    setForm(emptyAsset());
    setForm((f) => ({ ...f, assetStatusId: activeId }));
    setShowForm(true);
  };
  const openEdit = (a) => {
    setForm({
      id: a.id,
      purchaseDate: a.purchaseDate,
      name: a.name,
      supplier: a.supplier || "",
      category: a.category || "",
      purchaseAmount: String(a.purchaseAmount),
      businessUsePercentage: String(a.businessUsePercentage),
      receiptNumber: a.receiptNumber || "",
      assetStatusId: a.assetStatusId || "",
      disposalDate: a.disposalDate || "",
      taxCapitalAllowanceStatus: a.taxCapitalAllowanceStatus || "",
      linkedTransactionId: a.linkedTransactionId || "",
      notes: a.notes || "",
    });
    setShowForm(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.purchaseDate) return "Purchase Date is required";
    if (!form.name.trim()) return "Asset Name is required";
    const amt = parseFloat(form.purchaseAmount);
    if (isNaN(amt) || amt < 0) return "Purchase Amount must be a non-negative number";
    const bp = parseFloat(form.businessUsePercentage);
    if (isNaN(bp) || bp < 0 || bp > 100) return "Business-use % must be between 0 and 100";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        purchaseAmount: parseFloat(form.purchaseAmount) || 0,
        businessUsePercentage: parseFloat(form.businessUsePercentage) || 100,
      };
      if (form.id) {
        await db.updateAsset(form.id, payload);
        toast.success("Asset updated");
      } else {
        await db.createAsset(payload);
        toast.success("Asset added");
      }
      setShowForm(false);
      fetchAssets();
    } catch (e) { console.error(e); toast.error(e.message || "Could not save asset"); }
    finally { setSubmitting(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await db.deleteAsset(deleteTarget.id);
      toast.success("Asset deleted");
      setDeleteTarget(null); fetchAssets();
    } catch { toast.error("Failed to delete"); }
  };

  const statusName = (id) => statuses.find((s) => s.id === id)?.name || "—";
  const statusColor = (name) => {
    switch ((name || "").toLowerCase()) {
      case "active": return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "disposed": return "bg-slate-100 text-slate-600 border-slate-200";
      case "lost or damaged": return "bg-red-50 text-red-700 border-red-200";
      case "pending review": return "bg-amber-50 text-amber-800 border-amber-200";
      default: return "bg-slate-100 text-slate-600 border-slate-200";
    }
  };
  const linkedTxLabel = (id) => {
    if (!id) return null;
    const t = assetTransactions.find((x) => x.id === id);
    if (!t) return "Linked transaction";
    return `${t.txDate} · ${formatCurrency(t.amount)} · ${t.description || "—"}`;
  };

  const inputClass = "bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 px-3 text-sm";
  const labelClass = "text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block";
  const selectClass = `${inputClass} w-full appearance-none cursor-pointer`;

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="assets-title">
              Asset Register
            </h1>
            <p className="text-sm text-slate-600 mt-1">Equipment &amp; fixed assets — capital allowance tracking</p>
          </div>
          <Button onClick={openNew} className="bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full font-semibold h-11 px-5" data-testid="add-asset-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Asset
          </Button>
        </div>

        <ExportBar
          testIdPrefix="assets-export"
          buildReport={() => {
            const cols = [
              { key: "purchaseDate", label: "Purchase Date" },
              { key: "name", label: "Asset Name" },
              { key: "category", label: "Category" },
              { key: "supplier", label: "Supplier" },
              { key: "purchaseAmount", label: "Amount (RM)", type: "currency" },
              { key: "businessUsePercentage", label: "Business Use %" },
              { key: "statusName", label: "Status" },
              { key: "disposalDate", label: "Disposal Date" },
              { key: "taxCapitalAllowanceStatus", label: "Capital Allowance" },
              { key: "receiptNumber", label: "Receipt" },
              { key: "notes", label: "Notes" },
            ];
            const rowsExp = assets.map((a) => ({ ...a, statusName: statusName(a.assetStatusId) }));
            const totals = { purchaseDate: "TOTAL", purchaseAmount: rowsExp.reduce((s, r) => s + Number(r.purchaseAmount || 0), 0) };
            return {
              title: "Asset Register",
              subtitle: `As at ${new Date().toISOString().slice(0, 10)}`,
              filename: `Gao-Chill-Asset-Register-${new Date().getFullYear()}`,
              columns: cols,
              rows: rowsExp,
              totals,
              sections: [{ heading: "Assets", columns: cols, rows: rowsExp, totals }],
              metrics: [
                { label: "Total Assets", value: assets.length },
                { label: "Active Value", value: formatCurrency(activeValue) },
                { label: "Cumulative", value: formatCurrency(totals.purchaseAmount) },
              ],
              footnote: "Capital-allowance and disposal decisions require your own review. This report is for record preparation only.",
            };
          }}
        />

        <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 text-xs text-violet-800 flex items-start gap-2" data-testid="assets-notice">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Preventing double-counting:</strong> If you paid for the asset via a Transaction (type "Equipment / Asset Purchase"), link that transaction below. Cash Summary already sees the payment via the transaction; capital allowance decisions stay editable and are never auto-applied.
          </div>
        </div>

        <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm space-y-4" data-testid="assets-filters">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search asset name, supplier, category, notes…" className="w-full bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 pl-9 pr-9 text-sm" data-testid="assets-search" />
            {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1" data-testid="assets-search-clear"><X className="w-4 h-4" /></button>}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">Filter by Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass} data-testid="assets-status-filter">
              <option value="all">All Statuses</option>
              {statuses.map((s) => (<option key={s.id} value={s.id}>{s.name}{s.isArchived ? " (archived)" : ""}</option>))}
            </select>
          </div>
        </section>

        <div className="bg-gradient-to-br from-[#193f7d] to-[#122f5e] text-white rounded-2xl p-4 sm:p-5 shadow-lg grid grid-cols-3 gap-4" data-testid="assets-summary">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Total assets</p>
            <p className="text-xl font-mono font-bold mt-1" data-testid="assets-count">{assets.length}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Active value</p>
            <p className="text-xl font-mono font-bold mt-1" data-testid="assets-active-value">{formatCurrency(activeValue)}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Cumulative purchase</p>
            <p className="text-xl font-mono font-bold mt-1" data-testid="assets-total">{formatCurrency(total)}</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading…</div>
        ) : assets.length === 0 ? (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl" data-testid="assets-empty">
            <Boxes className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No assets recorded yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {assets.map((a) => {
              const st = statusName(a.assetStatusId);
              return (
                <div key={a.id} className="bg-white border border-slate-200 hover:border-[#193f7d]/40 rounded-2xl p-4 sm:p-5 shadow-sm" data-testid={`asset-row-${a.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono font-semibold text-slate-500">{a.purchaseDate}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${statusColor(st)}`}>{st}</span>
                        {a.linkedTransactionId && (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2 py-0.5">Linked to Tx</span>
                        )}
                      </div>
                      <p className="text-base font-semibold text-slate-900 mt-1 truncate">{a.name}</p>
                      <p className="text-xs text-slate-600 mt-1">
                        {a.category && <>{a.category} · </>}
                        {a.supplier && <>Supplier: {a.supplier}</>}
                      </p>
                      {a.linkedTransactionId && (
                        <p className="text-[11px] text-violet-700 mt-1">Linked payment: {linkedTxLabel(a.linkedTransactionId)}</p>
                      )}
                      {a.taxCapitalAllowanceStatus && (
                        <p className="text-[11px] text-slate-500 mt-1">Capital allowance: <span className="font-semibold">{a.taxCapitalAllowanceStatus}</span></p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xl font-mono font-bold text-slate-900">{formatCurrency(a.purchaseAmount)}</p>
                      {a.businessUsePercentage !== 100 && <p className="text-[10px] text-slate-500 mt-1">{a.businessUsePercentage}% business use</p>}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
                    <button onClick={() => openEdit(a)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#193f7d] hover:bg-slate-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d]" data-testid={`asset-edit-${a.id}`}>
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => setDeleteTarget(a)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-red-300 hover:bg-red-50 text-xs font-semibold text-slate-600 hover:text-red-700" data-testid={`asset-delete-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Asset Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center overflow-y-auto p-0 sm:p-6" data-testid="asset-form-dialog">
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl max-h-[95vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 sm:px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold text-[#193f7d]" style={{ fontFamily: "Outfit, sans-serif" }}>{form.id ? "Edit Asset" : "Add Asset"}</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-full hover:bg-slate-100 text-slate-500" data-testid="asset-form-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 sm:px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className={labelClass}>Purchase Date *</Label>
                  <DatePickerField value={form.purchaseDate} onChange={(v) => set("purchaseDate", v)} placeholder="Select date" testId="asset-date-picker" />
                </div>
                <div>
                  <Label className={labelClass}>Purchase Amount (RM) *</Label>
                  <Input type="number" step="0.01" min="0" value={form.purchaseAmount} onChange={(e) => set("purchaseAmount", e.target.value)} placeholder="0.00" className={inputClass} data-testid="asset-amount-input" />
                </div>
              </div>
              <div>
                <Label className={labelClass}>Asset Name *</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Panasonic Chiller CR3000" className={inputClass} data-testid="asset-name-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className={labelClass}>Category</Label>
                  <Input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Kitchen Equipment" className={inputClass} data-testid="asset-category-input" />
                </div>
                <div>
                  <Label className={labelClass}>Supplier</Label>
                  <Input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} placeholder="Optional" className={inputClass} data-testid="asset-supplier-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className={labelClass}>Status *</Label>
                  <select value={form.assetStatusId} onChange={(e) => set("assetStatusId", e.target.value)} className={selectClass} data-testid="asset-status-select">
                    <option value="">Select status…</option>
                    {statuses.filter((s) => !s.isArchived).map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                  </select>
                </div>
                <div>
                  <Label className={labelClass}>Business-use %</Label>
                  <Input type="number" step="1" min="0" max="100" value={form.businessUsePercentage} onChange={(e) => set("businessUsePercentage", e.target.value)} className={inputClass} data-testid="asset-business-use-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className={labelClass}>Receipt / Invoice No.</Label>
                  <Input value={form.receiptNumber} onChange={(e) => set("receiptNumber", e.target.value)} placeholder="Optional" className={inputClass} data-testid="asset-receipt-input" />
                </div>
                <div>
                  <Label className={labelClass}>Disposal Date</Label>
                  <DatePickerField value={form.disposalDate} onChange={(v) => set("disposalDate", v)} placeholder="If disposed" testId="asset-disposal-date-picker" />
                </div>
              </div>
              <div>
                <Label className={labelClass}>Tax / Capital Allowance Status</Label>
                <Input value={form.taxCapitalAllowanceStatus} onChange={(e) => set("taxCapitalAllowanceStatus", e.target.value)} placeholder="e.g. Small Value Asset · Full CA claimed · Under review" className={inputClass} data-testid="asset-tax-status-input" />
              </div>
              <div>
                <Label className={labelClass}>Link to Cash-Payment Transaction (prevents double-counting)</Label>
                <select value={form.linkedTransactionId} onChange={(e) => set("linkedTransactionId", e.target.value)} className={selectClass} data-testid="asset-linked-tx-select">
                  <option value="">— Not linked —</option>
                  {assetTransactions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.txDate} · {formatCurrency(t.amount)} · {t.description || t.supplierCustomer || "—"}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-500 mt-1">If you already logged this payment under Transactions → Equipment / Asset Purchase, pick it here so it isn't counted twice in the Cash Summary.</p>
              </div>
              <div>
                <Label className={labelClass}>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Optional" className={`${inputClass} h-auto py-2`} data-testid="asset-notes-input" />
              </div>
              <div>
                <Label className={labelClass}>Receipt Attachment</Label>
                <ReceiptUploader entityType="asset" entityId={form.id} />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 sm:px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-full" data-testid="asset-form-cancel">Cancel</Button>
              <Button onClick={handleSave} disabled={submitting} className="bg-[#193f7d] hover:bg-[#122f5e] text-white rounded-full font-semibold px-6" data-testid="asset-form-save">
                {submitting ? "Saving…" : form.id ? "Save Changes" : "Save Asset"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
            <AlertDialogDescription>This removes the asset record only. Any linked cash transaction remains untouched.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="asset-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Assets;
